/**
 * Auto-populate algorithms for the Buildings page (SF-69).
 * Pure functions enabling clean isolated testing.
 */

export interface AutoFillResult {
  quantities: Record<string, number>;
  summary: string;
  type: 'success' | 'scaled' | 'met' | 'empty';
}

/**
 * Compute build quantities to reach strategy targets.
 *
 * Fills deficits proportionally if barren land is insufficient,
 * using largest-remainder rounding so totals always sum correctly.
 */
export function computeAutoFill(
  strategy: Record<string, number>,   // target percentages (0-100)
  totalLand: number,
  owned: Record<string, number>,
  inProgress: Record<string, number>,
  barren: number
): AutoFillResult {
  if (totalLand <= 0) {
    return { quantities: {}, summary: 'No land', type: 'empty' };
  }

  // Compute deficits for each building with a strategy target
  const deficits: Record<string, number> = {};
  for (const [buildingId, targetPct] of Object.entries(strategy)) {
    if (targetPct <= 0) continue;
    const targetCount = Math.floor((totalLand * targetPct) / 100);
    const current = (owned[buildingId] ?? 0) + (inProgress[buildingId] ?? 0);
    const deficit = Math.max(0, targetCount - current);
    if (deficit > 0) {
      deficits[buildingId] = deficit;
    }
  }

  const totalDeficit = Object.values(deficits).reduce((sum, d) => sum + d, 0);

  if (totalDeficit === 0) {
    return { quantities: {}, summary: 'All targets met', type: 'met' };
  }

  if (barren <= 0) {
    return { quantities: {}, summary: 'No barren land', type: 'empty' };
  }

  if (totalDeficit <= barren) {
    // Enough barren land — fill all deficits directly
    return {
      quantities: { ...deficits },
      summary: `Fill ${totalDeficit} acres to reach targets`,
      type: 'success',
    };
  }

  // Not enough barren land — proportionally scale deficits with largest-remainder rounding
  const scale = barren / totalDeficit;
  const buildingIds = Object.keys(deficits);

  // Compute floor values and remainders
  const floors: Record<string, number> = {};
  const remainders: { id: string; remainder: number }[] = [];
  let allocated = 0;

  for (const id of buildingIds) {
    const exact = deficits[id] * scale;
    floors[id] = Math.floor(exact);
    remainders.push({ id, remainder: exact - Math.floor(exact) });
    allocated += floors[id];
  }

  // Distribute remaining acres by largest remainder
  const remaining = barren - allocated;
  remainders.sort((a, b) => b.remainder - a.remainder);
  for (let i = 0; i < remaining; i++) {
    floors[remainders[i].id] = (floors[remainders[i].id] ?? 0) + 1;
  }

  // Remove zeros
  const quantities: Record<string, number> = {};
  for (const [id, qty] of Object.entries(floors)) {
    if (qty > 0) quantities[id] = qty;
  }

  return {
    quantities,
    summary: `Scaled to ${barren} available acres (deficit was ${totalDeficit})`,
    type: 'scaled',
  };
}

/**
 * Compute raze quantities to reach strategy targets.
 * Only considers completed (owned) buildings — in-progress buildings cannot be razed.
 */
export function computeAutoRaze(
  strategy: Record<string, number>,  // target percentages (0-100)
  totalLand: number,
  owned: Record<string, number>
): Record<string, number> {
  if (totalLand <= 0) return {};

  const quantities: Record<string, number> = {};
  for (const [buildingId, targetPct] of Object.entries(strategy)) {
    const targetCount = Math.floor((totalLand * targetPct) / 100);
    const ownedCount = owned[buildingId] ?? 0;
    const excess = Math.max(0, ownedCount - targetCount);
    if (excess > 0) {
      quantities[buildingId] = excess;
    }
  }

  // Also include buildings with no strategy target but owned buildings (raze to 0)
  // — actually per spec, auto-raze only fills buildings where owned > target,
  //   so we only process buildings that have strategy targets.
  return quantities;
}
